import { PORTAL_PATH } from "@/constants/route";
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, isAxiosError } from "axios";
import { Message } from "@ks/kwai-ui";

/**
 * 默认出错消息文案
 */
const DEFAULT_ERROR_MESSAGE = "请求出错了，请稍后再试";
/**
 * 登陆相关出错文案
 */
const LOGIN_STATUS_EXCEPTION_ERROR_MESSAGE = "登录后即可查看相关信息，请在首页进行登录";
/**
 * 未获取到登陆态后端错误码
 */
export const LOGIN_STATUS_ERROR_CODE = [109, 6001, 6002];

const config: AxiosRequestConfig = {
    timeout: 30e3,
    headers: {
        Accept: "application/json",
    },
    withCredentials: true,
};

export interface AjaxResponse<T> {
    result: number; // 后端错误码，为 1 表示成功，别的常见错误码见上方错误码定义
    data: T;
    error_msg?: string;
    loginUrl?: string;
}

function getAxiosInstance<T>(): AxiosInstance {
    const instance: AxiosInstance = axios.create(config);

    instance.interceptors.response.use(
        (res: AxiosResponse<AjaxResponse<T>>) => {
            if (LOGIN_STATUS_ERROR_CODE.includes(res.data.result)) {
                // 门户页无需自动跳转登录页
                if (location.pathname !== PORTAL_PATH) {
                    location.href = res.data.loginUrl as string;
                }

                return Promise.reject({
                    message: LOGIN_STATUS_EXCEPTION_ERROR_MESSAGE,
                    config: res.config,
                    code: String(res.data.result),
                    loginUrl: res.data.loginUrl,
                });
            }

            if (res.data.result !== undefined && 1 !== res.data.result) {
                const message = res.data.error_msg || DEFAULT_ERROR_MESSAGE;
                const code = String(res.data.result);
                Message({
                    message: `${message}`,
                    type: "warning",
                });

                return Promise.reject({
                    message,
                    config: res.config,
                    code,
                } as AxiosError);
            }

            return res;
        },
        (error) => {
            if (isAxiosError(error)) {
                Message({
                    message: error.message,
                    type: "warning",
                });
            } else {
                Message({
                    message: DEFAULT_ERROR_MESSAGE,
                    type: "warning",
                });
            }
            return Promise.reject(new Error(DEFAULT_ERROR_MESSAGE));
        }
    );

    return instance;
}

export default {
    post: function <T>(url: string, data: object = {}, config: AxiosRequestConfig = {}): Promise<AjaxResponse<T>> {
        const instance: AxiosInstance = getAxiosInstance<T>();
        return new Promise((resolve, reject) => {
            instance
                .request<AjaxResponse<T>>({
                    ...config,
                    method: "POST",
                    url,
                    data,
                })
                .then((data: AxiosResponse<AjaxResponse<T>>) => {
                    resolve(data.data);
                }, reject);
        });
    },
    get: function <T>(url: string, data: object = {}, config: AxiosRequestConfig = {}): Promise<AjaxResponse<T>> {
        const instance: AxiosInstance = getAxiosInstance<T>();

        return new Promise((resolve, reject) => {
            instance
                .request<AjaxResponse<T>>({
                    ...config,
                    method: "GET",
                    url,
                    data,
                })
                .then((data: AxiosResponse<AjaxResponse<T>>) => {
                    resolve(data.data);
                }, reject);
        });
    },
};
